// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from "firebase/auth";
import { 
  initializeFirestore,
  persistentLocalCache, 
  persistentMultipleTabManager,
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  serverTimestamp 
} from "firebase/firestore";
import { getStorage } from 'firebase/storage';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Analytics
let analytics = null;
if (typeof window !== 'undefined') {
  analytics = getAnalytics(app);
}

// Initialize Firebase services
export const auth = getAuth(app);
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  })
});
export const storage = getStorage(app);

// Configure auth persistence explicitly
import { setPersistence, browserLocalPersistence, inMemoryPersistence } from 'firebase/auth';

// Set auth persistence to MEMORY inside iframe to prevent IndexedDB/session conflicts with parent window
if (window.self !== window.top) {
  setPersistence(auth, inMemoryPersistence).catch(err => {
    console.error('Failed to set Firebase Auth persistence to MEMORY in iframe:', err);
  });
} else {
  setPersistence(auth, browserLocalPersistence).catch(err => {
    console.error('Failed to set Firebase Auth persistence to LOCAL:', err);
  });
}


// Initialize Google Auth Provider
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Detect if user is in an embedded/insecure browser (Instagram, Facebook, LinkedIn, etc.)
export const isEmbeddedBrowser = () => {
  // Always get fresh user agent - no caching
  const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera || '';
  const ua = userAgent.toLowerCase();
  
  // Debug: Log user agent to console (can be removed in production)
  console.log('[EmbeddedBrowser Detection] User Agent:', userAgent);
  
  // Check for common in-app browsers - comprehensive detection
  const isInstagram = ua.includes('instagram') || /instagram/i.test(userAgent);
  
  // Facebook detection - multiple variations
  const isFacebook = ua.includes('fban') || ua.includes('fbav') || ua.includes('fb_iab') || 
                     ua.includes('fbios') || ua.includes('fb4a') || ua.includes('facebook') ||
                     /FBAN|FBAV|FB_IAB|FBIOS|FB4A/i.test(userAgent);
  
  const isTwitter = ua.includes('twitter') || /twitter/i.test(userAgent);
  
  // LinkedIn detection - comprehensive
  const isLinkedIn = ua.includes('linkedin') || ua.includes('lclclient') || 
                     ua.includes('[linkedinapp]') || ua.includes('linkedinapp') ||
                     /LinkedIn/i.test(userAgent);
  
  const isTikTok = ua.includes('tiktok') || ua.includes('bytedance') || ua.includes('musical_ly');
  const isSnapchat = ua.includes('snapchat');
  const isLine = ua.includes('line/') || ua.includes('line ');
  const isWeChat = ua.includes('micromessenger') || ua.includes('wechat');
  const isWhatsApp = ua.includes('whatsapp');
  const isTelegram = ua.includes('telegram') || ua.includes('tgweb');
  const isPinterest = ua.includes('pinterest');
  const isSlack = ua.includes('slack');
  const isDiscord = ua.includes('discord');
  const isMessenger = ua.includes('messenger');
  const isReddit = ua.includes('reddit');
  
  // Generic WebView detection - multiple patterns
  const isWebView = ua.includes('wv') || ua.includes('webview') || 
                    ua.includes('; wv)') || ua.includes('webviewassetloader');
  
  // iOS WebView detection (UIWebView/WKWebView when not Safari)
  // iOS Safari always has "Safari" in the UA, WebViews often don't
  const isIOS = /iphone|ipod|ipad/i.test(ua);
  const isIOSWebView = isIOS && !ua.includes('safari');
  
  // Additional iOS check: Safari version missing but AppleWebKit present
  const isIOSAppBrowser = isIOS && ua.includes('applewebkit') && !ua.includes('safari/');
  
  // Android WebView detection
  const isAndroid = ua.includes('android');
  const isAndroidWebView = isAndroid && (ua.includes('wv') || ua.includes('; wv)'));
  
  // Check for Chrome Custom Tabs (often used by apps but still restricted)
  const isChromeCustomTab = isAndroid && ua.includes('chrome') && !ua.includes('chrome mobile');
  
  // Check if standalone PWA or in-app browser (no Safari/Chrome identifier)
  const isMobile = /iphone|ipad|ipod|android/i.test(ua);
  const hasNoRealBrowser = isMobile && 
    !ua.includes('safari/') && 
    !ua.includes('chrome/') && 
    !ua.includes('firefox/') && 
    !ua.includes('edg/') &&
    !ua.includes('samsungbrowser');
  
  // Check for restricted features that indicate embedded browser
  let hasRestrictedFeatures = false;
  try {
    // Some embedded browsers restrict certain APIs
    hasRestrictedFeatures = !window.indexedDB || 
                            (typeof window.orientation !== 'undefined' && !document.cookie);
  } catch (e) {
    hasRestrictedFeatures = true;
  }
  
  const result = isInstagram || isFacebook || isTwitter || isLinkedIn || isTikTok || 
         isSnapchat || isLine || isWeChat || isWhatsApp || isTelegram || isPinterest ||
         isSlack || isDiscord || isMessenger || isReddit ||
         isWebView || isIOSWebView || isIOSAppBrowser || isAndroidWebView || hasNoRealBrowser;
  
  // Debug log the detection result
  console.log('[EmbeddedBrowser Detection] Result:', result, {
    isInstagram, isFacebook, isTwitter, isLinkedIn, isTikTok, 
    isSnapchat, isLine, isWeChat, isWhatsApp, isTelegram, isPinterest,
    isSlack, isDiscord, isMessenger, isReddit,
    isWebView, isIOSWebView, isIOSAppBrowser, isAndroidWebView, hasNoRealBrowser
  });
  
  return result;
};

// Auth functions - use popup for Google sign-in
export const signInWithGoogle = async () => {
  // Check if in embedded browser - Google blocks OAuth from these
  if (isEmbeddedBrowser()) {
    const error = new Error('Google Sign-In is not supported in this browser. Please open in Safari or Chrome.');
    error.code = 'auth/embedded-browser';
    throw error;
  }
  
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result;
  } catch (error) {
    throw error;
  }
};

export const signInWithEmail = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const registerWithEmail = (email, password) => {
  return createUserWithEmailAndPassword(auth, email, password);
};

export const signOutUser = () => {
  return signOut(auth);
};

export const resetPassword = (email) => {
  return sendPasswordResetEmail(auth, email).catch((error) => {
    console.error('Failed to send password reset email:', error);
    throw error;
  });
};

export const changePassword = async (currentPassword, newPassword) => {
  const user = auth.currentUser;
  if (!user || !user.email) throw new Error('No authenticated user');
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
};

export const updateUserProfile = (user, profileData) => {
  return updateProfile(user, profileData);
};

export const sendVerificationEmail = async (user) => {
  if (!user) {
    console.error('sendVerificationEmail: No user provided');
    return Promise.reject(new Error('No user to verify'));
  }
  
  try {
    // Action code settings for the verification email link
    const actionCodeSettings = {
      // URL you want to redirect back to after verification
      url: window.location.origin + '/login',
      handleCodeInApp: false
    };
    
    await sendEmailVerification(user, actionCodeSettings);
    console.log('Verification email sent successfully to:', user.email);
    return true;
  } catch (error) {
    console.error('Failed to send verification email:', error.code, error.message);
    throw error;
  }
};

// Firestore functions
export const createUserDocument = async (userAuth, additionalData = {}) => {
  if (!userAuth) {
    return null;
  }
  
  const userDocRef = doc(db, 'users', userAuth.uid);
  
  try {
    const userSnapshot = await getDoc(userDocRef);
    
    if (!userSnapshot.exists()) {
      const { displayName, email, photoURL } = userAuth;
      const createdAt = serverTimestamp();
      
      await setDoc(userDocRef, {
        displayName: displayName || '',
        email,
        photoURL: photoURL || '',
        createdAt,
        lastLoginAt: serverTimestamp(),
        cvCount: 0,
        plan: 'free',
        maxCVs: 3,
        ...additionalData
      });
      console.log('✅ User document created');
    } else {
      // Update last login time
      await updateDoc(userDocRef, {
        lastLoginAt: serverTimestamp()
      });
    }
    
    return userDocRef;
  } catch (error) {
    throw error;
  }
};

export const getUserDocument = async (uid) => {
  if (!uid) {
    return null;
  }
  
  
  try {
    const userDocRef = doc(db, 'users', uid);
    const userSnapshot = await getDoc(userDocRef);
    
    if (userSnapshot.exists()) {
      const userData = { id: userSnapshot.id, ...userSnapshot.data() };
      return userData;
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

export const updateUserDocument = async (uid, data) => {
  if (!uid) return;
  
  
  try {
    const userDocRef = doc(db, 'users', uid);
    await updateDoc(userDocRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    throw error;
  }
};

export const upgradeToPro = async (uid) => {
  if (!uid) return;
  
  
  try {
    const userDocRef = doc(db, 'users', uid);
    await updateDoc(userDocRef, {
      plan: 'pro',
      maxCVs: -1, // Unlimited
      upgradedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    throw error;
  }
};

export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, (user) => {
    callback(user);
  });
};

export { analytics }; 